import React, {useEffect, useState, ChangeEvent} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {axios} from '@/lib/axios';
import {FieldErrors, SubmitHandler, useForm, useWatch} from "react-hook-form";
import {basic_info_items, login_info_items} from "@/containers/basicInfo/BasicInfoDetail";
import {getBasicInfo, updateBasicData} from "@/api/baseData";
import {useLoadingStore} from "@/stores/loading";
import ConfirmModal from "@/components/ConfirmModal";
import {TextBox} from "@/components/Form/TextBox";
import {
  IS_AGREE,
  BUSINESS_ENTITY_TYPE,
  BILLING_POST_TYPE,
  MESSAGE_NO_E39,
  MESSAGE_NO_E08,
  MESSAGE_NO_E35,
  MESSAGE_NO_E37,
  CUSTOMER_PAYMENT_TYPE_FREE,
  CUSTOMER_STATUS_UNAPPROVED,
} from "@/config";
import {useUserStore} from '@/stores/user';
import {isEqual} from "lodash";

export interface Details {
  id?: number,
  customer_number?: string,
  customer_type_id?: string,
  customer_payment_type_id?: string,
  group_name?: string,
  station_name?: string,
  department_name?: string,
  unit_name?: string,
  section_name?: string,
  position?: string,
  last_name?: string,
  first_name?: string,
  reading_last_name?: string,
  reading_first_name?: string,
  phone_number?: string,
  emergency_phone_number?: string,
  zipcode?: string,
  pref?: string,
  city?: string,
  house_number?: string,
  building_name?: string,
  email_address?: string,
  customer_group_type_id?: string,
  approval_status?: string,
}

interface Details2 {
  id?: number,
  customer_number?: string,
  customer_type_id?: string,
  customer_group_type_id?: string,
  corporate_number?: string,
  group_name?: string,
}

interface Info {
  id?: number,
  is_personal_information_handling?: string,
  login_id?: string,
  password?: string,
}

export default function BasicInfoEdit() {
  const navigate = useNavigate();
  const [businessType, setBusinessType] = useState<string | null>(null);
  const [billingType, setBillingType] = useState<string | null>(null);
  const [isAgree, setIsAgree] = useState<string | null>(null);
  const setLoading = useLoadingStore(state => state.setLoading);
  const [details, setDetails] = useState<Details>();
  const [details2, setDetails2] = useState<Details2>();
  const [info, setInfo] = useState<Info[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [isShowBackModal, setIsShowBackModal] = useState(false);
  const [isCancelModal, setIsShowCancelModal] = useState(false);
  const [isShowErrorModal, setIsShowErrorModal] = useState(false);
  const [isShowResultModal, setIsShowResultModal] = useState(false);
  const [isTabChangeModal, setIsShowTabChangeModal] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string | null>(null);
  const user = useUserStore(state => state.user);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: {errors, isDirty}
  } = useForm<Details>(
    {
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
      criteriaMode: 'all',
      defaultValues: details,
    }
  );
  const hasErrors = Object.keys(errors).length > 0;

  const onClickCancel = () => {
    setIsShowCancelModal(true);
  }

  const onClickSave = () => {
    handleSubmit(onSubmit)();
  }

  const [nextPath, setNextPath] = useState<string>('');

  const onClickTab = (e: React.MouseEvent, path: string) => {
    // Linkのデフォルトの遷移をキャンセル
    e.preventDefault();
    if (isDirty) {
      // 変更がある場合は遷移先を保存してモーダル表示
      setNextPath(path);
      setIsShowTabChangeModal(true);
    } else {
      // 変更がない場合はそのまま遷移
      navigate(path);
    }
  };  

  const onSubmit: SubmitHandler<Details> = (data) => {
    setEmailErrorMessage(null);
    setLoading(true)
    const response = updateBasicData(data)
    response.then(result => {
      setResultMessage(result.data.response.success);
      //navigate(-1)
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E37);
    }).finally(() => {
      setLoading(false);
    });

  }
  // 郵便番号⇒住所検索
  const handleZipcodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newZipcode = e.target.value;

    // APIリクエストを送信
    axios.get(`/api/v1/proxy/getAddress?zipcode=${newZipcode}`)
      .then(response => {
        const results = response.data.results;
        if (results) {
          setValue('pref', results[0].address1);
          setValue('city', results[0].address2);
          setValue('house_number', results[0].address3);
          setValue('building_name', '');
        } else {
          alert('該当する住所が見つかりませんでした');
        }
      })
      .catch(error => {
        console.error('Error fetching address:', error);
      });
  };


  useEffect(() => {
    setLoading(true)
    const response = getBasicInfo();
    response.then(result => {
      const data = result.data.details;
      setDetails(data);
      setDetails2(data);
      reset(data);
      
      setInfo(result.data.info);
      const entry_type = BUSINESS_ENTITY_TYPE.find((output, index) => {
        return output.value == result.data.details['business_entity_type'];
      });
      setBusinessType(entry_type?.label || '');

      const billing_type = BILLING_POST_TYPE.find((output, index) => {
        return output.value == result.data.details['billing_post_type'];
      });
      setBillingType(billing_type?.label || '');
      setIsAgree(IS_AGREE[result.data.info[0]['is_personal_information_handling']]);
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E39);
    }).finally(() => {
      setLoading(false);
    });
  }, [])

  useEffect(() => {
    if (errorMessage) {
      setIsShowErrorModal(true);
    }
    if (resultMessage) {
      setIsShowResultModal(true);
    }
  }, [errorMessage, resultMessage])

  return (
    <div>
      <div className="sub-header">
        <ul className="uk-tab uk-margin">
          <li><Link to="/drm_pf/api" onClick={(e) => onClickTab(e, "/drm_pf/api")}>利用料</Link></li>
          <li className="uk-active"><Link to="/basic_info">基本情報</Link></li>
          <li><Link to="#" onClick={(e) => onClickTab(e, "/drm_pf/info")}>DRM-PF</Link></li>
          <li><Link to="/ticket" onClick={(e) => onClickTab(e, "/ticket")}>DRM-Chat</Link></li>
        </ul>
      </div>
      <div className="container uk-margin-large-bottom">
        <div className="uk-text-right attribute-tab">
          <button className="uk-button--m uk-button-cancel" onClick={onClickCancel}>キャンセル</button>
          <button className="uk-button--m uk-button-refer uk-margin-left" onClick={onClickSave}
                  disabled={!isDirty}>保存
          </button>
        </div>
        <div className="info-tbl_wrap">
        <ConfirmModal
          text='画面遷移しますか？入力したデータは登録されません。'
          confirmButtonText="OK"
          isShow={isTabChangeModal}
          onConfirm={() => {
            setIsShowTabChangeModal(false);
            navigate(nextPath);
          }}
          onCancel={() => {
            setIsShowTabChangeModal(false);
          }}
        />
          <ConfirmModal
            text='キャンセルしますか？入力したデータは登録されません。'
            confirmButtonText="OK"
            isShow={isCancelModal}
            onConfirm={() => {
              navigate(`/basic_info`)
            }}
            onCancel={() => {
              setIsShowCancelModal(false);
            }}
          />
          {resultMessage &&
              <ConfirmModal
                  text={resultMessage}
                  confirmButtonText="OK"
                  isShow={isShowResultModal}
                  onConfirm={() => {
                    setIsShowResultModal(false);
                    setResultMessage(null);
                    navigate("/basic_info");
                  }}
              />
          }
          {errorMessage &&
              <ConfirmModal
                  text={errorMessage}
                  confirmButtonText="OK"
                  isShow={isShowErrorModal}
                  onConfirm={() => {
                    setIsShowErrorModal(false);
                    setErrorMessage(null);
                    navigate("/basic_info");
                  }}
              />
          }
          <form>
            {hasErrors && <div className="error-box">入力エラーがあります</div>}
            <div className="precaution">※は必須です</div>

            <table className="info-tbl uk-width-1-1">
              <tbody>
              <tr>
                <th className="label">顧客番号</th>
                <td className="content">{details2?.customer_number}</td>
              </tr>
              <tr>
                <th className="label">道路管理者区分</th>
                <td className="content">{details2?.customer_type_id}</td>
              </tr>
              <tr>
                <th className="label">団体区分</th>
                <td className="content">{details2?.customer_group_type_id}</td>
              </tr>
              <tr>
                <th className="label">法人・団体名</th>
                <td className="content">
                  {details?.approval_status == CUSTOMER_STATUS_UNAPPROVED || details?.customer_payment_type_id == CUSTOMER_PAYMENT_TYPE_FREE ? (
                    <>
                      <div>
                        <TextBox
                          name="group_name"
                          className="uk-width-1-1"
                          register={register}
                          rules={{
                            required: MESSAGE_NO_E08,
                            maxLength: {value: 100, message: MESSAGE_NO_E35(100)},
                          }}
                          errors={errors}
                          placeholder={"法人・団体名を入力してください"}
                        />
                      </div>
                    </>

                  ) : (
                    <>
                      <div>
                        {details2?.group_name}
                      </div>
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th className="label">局名</th>
                <td className="content">
                  <TextBox
                    name="station_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      maxLength: {value: 100, message: MESSAGE_NO_E35(100)},
                    }}
                    errors={errors}
                    placeholder={"局名を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">部署名・所属<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="department_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 100, message: MESSAGE_NO_E35(100)},
                    }}
                    errors={errors}
                    placeholder={"部署名・所属を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">課名</th>
                <td className="content">
                  <TextBox
                    name="section_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      maxLength: {value: 100, message: MESSAGE_NO_E35(100)},
                    }}
                    errors={errors}
                    placeholder={"課名を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">室名または係名</th>
                <td className="content">
                  <TextBox
                    name="unit_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      maxLength: {value: 100, message: MESSAGE_NO_E35(100)},
                    }}
                    errors={errors}
                    placeholder={"室名または係名を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">役職</th>
                <td className="content">
                  <TextBox
                    name="position"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      maxLength: {value: 100, message: MESSAGE_NO_E35(100)},
                    }}
                    errors={errors}
                    placeholder={"役職を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">姓<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="last_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 30, message: MESSAGE_NO_E35(30)},
                    }}
                    errors={errors}
                    placeholder={"姓を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">名<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="first_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 30, message: MESSAGE_NO_E35(30)},
                    }}
                    errors={errors}
                    placeholder={"名を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">せい<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="reading_last_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 50, message: MESSAGE_NO_E35(50)},
                    }}
                    errors={errors}
                    placeholder={"せいを入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">めい<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="reading_first_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 50, message: MESSAGE_NO_E35(50)},
                    }}
                    errors={errors}
                    placeholder={"めいを入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">電話番号<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="phone_number"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 20, message: MESSAGE_NO_E35(20)},
                    }}
                    errors={errors}
                    placeholder={"電話番号を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">緊急連絡先（携帯電話等）</th>
                <td className="content">
                  <TextBox
                    name="emergency_phone_number"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      maxLength: {value: 20, message: MESSAGE_NO_E35(20)},
                    }}
                    errors={errors}
                    placeholder={"緊急連絡先（携帯電話等）を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">郵便番号<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="zipcode"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 7, message: MESSAGE_NO_E35(7)},
                    }}
                    errors={errors}
                    placeholder={"郵便番号を入力してください"}
                    onBlur={handleZipcodeChange}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">都道府県<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="pref"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 30, message: MESSAGE_NO_E35(30)},
                    }}
                    errors={errors}
                    placeholder={"都道府県を入力してください"}
                    disabled={true}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">市区町村<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="city"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 56, message: MESSAGE_NO_E35(56)},
                    }}
                    errors={errors}
                    placeholder={"市区町村を入力してください"}
                    disabled={true}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">町名番地<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="house_number"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 56, message: MESSAGE_NO_E35(56)},
                    }}
                    errors={errors}
                    placeholder={"町名番地を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">建物名・部屋番号</th>
                <td className="content">
                  <TextBox
                    name="building_name"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      maxLength: {value: 60, message: MESSAGE_NO_E35(60)},
                    }}
                    errors={errors}
                    placeholder={"建物名・部屋番号を入力してください"}
                  />
                </td>
              </tr>
              <tr>
                <th className="label">メールアドレス<span className="precaution">※</span></th>
                <td className="content">
                  <TextBox
                    name="email_address"
                    className="uk-width-1-1"
                    register={register}
                    rules={{
                      required: MESSAGE_NO_E08,
                      maxLength: {value: 254, message: MESSAGE_NO_E35(254)},
                    }}
                    errors={errors}
                    placeholder={"メールアドレスを入力してください"}
                  />
                </td>
              </tr>
              </tbody>
            </table>

            <table className="info-tbl uk-width-1-1">
              {info.map((item, index) => {
                return (
                  <tbody key={item.id}>
                  <tr>
                    <th className="label">個人情報取扱い</th>
                    <td className="content">{isAgree}</td>
                  </tr>
                  <tr>
                    <th className="label">ログインid</th>
                    <td className="content">{item.login_id}</td>
                  </tr>
                  <tr>
                    <th className="label">
                      パスワード<br/>
                      <span className="precaution">※変更時のみ入力してください</span>
                    </th>
                    <td className="content">
                      <span className="precaution">※パスワードは編集できません</span>
                    </td>
                  </tr>
                  </tbody>
                );
              })}
            </table>
          </form>
        </div>

      </div>
    </div>
  );
}